/**
 * Path Constants
 * 
 * Single source of truth for all routes.
 * Use these constants everywhere to prevent 404s and broken links.
 * 
 * Architecture:
 * - MY: Track START → /my/* (Client Shell)
 * - APP: Track PARTNERS → /app/* (Partners Shell, formerly HQ)
 * - ENTERPRISE: Track ENTERPRISE → /enterprise/* (Governance Shell)
 */

/** 
 * APP routes (Partners HQ) - formerly /hq, now /app
 * For PARTNER_HQ account types
 */
export const APP = {
  root: "/app",
  dashboard: "/app",
  accounts: "/app/accounts",
  account: (id: string) => `/app/accounts/${id}`,
  leads: "/app/leads",
  funnels: "/app/funnels",
  campaigns: "/app/campaigns",
  crm: "/app/crm",
  queue: "/app/queue",
  meetings: "/app/meetings",
  agents: "/app/agents",
  kb: "/app/kb",
  kbSkill: (slug: string) => `/app/kb/${slug}`,
  chatInbox: "/app/chat-inbox",
  integrations: "/app/integrations",
  events: "/app/events",
  team: "/app/team",
  settings: "/app/settings",
  shipGates: "/app/ship-gates",
  subAccounts: "/app/sub-accounts",
  calendar: "/app/calendar",
  qrCodes: "/app/qr-codes",
  acquisition: "/app/acquisition",
  salesHub: "/app/sales-hub",
  phone: "/app/phone",
  connectors: "/app/connectors",
  /** Stack detail in HQ context */
  stack: (slug: string) => `/stacks/${slug}`,
  /** Marketplace section (uses workspace section) */
  marketplace: "/app",
} as const;

/**
 * HQ routes - LEGACY alias for APP
 * @deprecated Use APP instead
 */
export const HQ = APP;

/** Sub-Account (Reseller) paths - LEGACY @deprecated Use APP instead */
export const RESELLER = {
  dashboard: "/my-sales",
  products: "/reseller/products",
  productsNew: "/reseller/products/new",
  funnels: "/reseller/funnels",
  funnelsNew: "/reseller/funnels/new",
} as const;

/** 
 * MY routes — now aliases to /app/* for unified platform
 * For START account types (client shell)
 */
export const MY = {
  root: "/app",
  dashboard: "/app",
  settings: "/app/settings/profile",
  career: "/my/career",
  
  /** Upgrade tier */
  upgrade: "/upgrade",
  /** Stack detail */
  stack: (slug: string) => `/stacks/${slug}`,
  /** Marketplace */
  marketplace: "/app/marketplace",
  /** Marketplace (Flows & Suites) */
  ebits: "/app/ebits",
  /** Runs (Flow executions) */
  runs: "/app/runs",
  /** Outputs / Werkbewijzen */
  outputs: "/app/outputs",
  /** Start new run with stack/ebit context */
  newRun: (stackSlug?: string, ebitId?: string) => {
    const params = new URLSearchParams();
    if (stackSlug) params.set("stack", stackSlug);
    if (ebitId) params.set("ebit", ebitId);
    const query = params.toString();
    return `/app/runs/new${query ? `?${query}` : ""}`;
  },
  /** Run detail view */
  runDetail: (runId: string) => `/app/runs/${runId}`,
  /** Tasks view with optional stack filter */
  tasks: "/app/runs",
  /** Tasks view for specific stack */
  tasksForStack: (stackSlug: string) => `/app/runs?stack=${stackSlug}`,
} as const;

/**
 * ENTERPRISE routes - /enterprise/*
 * For ENTERPRISE account types (governance shell)
 */
export const ENTERPRISE = {
  root: "/enterprise",
  dashboard: "/enterprise",
  governance: "/enterprise/governance",
  sso: "/enterprise/sso",
  scim: "/enterprise/scim",
  audit: "/enterprise/audit",
  dataRetention: "/enterprise/data-retention",
  customRoles: "/enterprise/custom-roles",
  settings: "/enterprise/settings",
} as const;

/** Public catalog paths (legacy routes redirect to /app) */
export const CATALOG = {
  ebits: "/app/marketplace",
  ebit: (slug: string) => `/app/marketplace`,
  stacks: "/app/marketplace",
  stack: (slug: string) => `/stacks/${slug}`,
  managed: "/managed",
  pricing: "/pricing",
} as const;

/** Build a scoped URL with account context */
export function buildAccountScopedUrl(basePath: string, accountId: string | null): string {
  if (!accountId) return basePath;
  return `${basePath}?account=${accountId}`;
}

/** Build stamp for version verification */
export const BUILD_STAMP = new Date().toISOString().slice(0, 16).replace("T", "-");

/**
 * Get the default route for an account type
 */
export function getDefaultRouteForAccountType(accountType: 'START' | 'PARTNER_HQ' | 'ENTERPRISE'): string {
  switch (accountType) {
    case 'ENTERPRISE':
      return ENTERPRISE.root;
    case 'PARTNER_HQ':
      return APP.root;
    case 'START':
    default:
      return APP.root; // Unified: all account types land on /app
  }
}
